import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton, DeleteButton } from "@refinedev/mui";
import DevicesIcon from '@mui/icons-material/Devices';
import StoreIcon from '@mui/icons-material/Store';

import { TableCell } from "@shared/components";
import { Company } from "@modules/companies/types";
import { Agreement } from "@modules/agreements/types";
import { User } from "@modules/users/types";
import { Discount } from "@modules/discounts/types";

interface UsageColumnsProps {
  onRefetch: () => void;
  translate: (key: string) => string;
  companies?: Company[];
  agreements?: Agreement[];
  users?: User[];
  discounts?: Discount[];
}

export const useUsagesListColumns = ({
  onRefetch,
  translate,
  companies,
  agreements,
  users,
  discounts
}: UsageColumnsProps): GridColDef[] => {
  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: translate("v1/usage_admin.fields.id"),
        width: 80,
      },
      {
        field: "online",
        headerName: translate("v1/usage_admin.fields.online"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value: isOnline }) => (
          <TableCell.Container align="center">
            {isOnline ? <DevicesIcon /> : <StoreIcon />}
          </TableCell.Container>
        ),
      },
      {
        field: "company",
        headerName: translate("v1/usage_admin.fields.company"),
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/companies/edit/${value}`}
            value={companies?.find(company => company.id === value)?.name}
          />
        ),
      },
      {
        field: "agreement",
        headerName: translate("v1/usage_admin.fields.agreement"),
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/agreements/edit/${value}`}
            value={agreements?.find(agreement => agreement.id === value)?.name}
          />
        ),
      },
      {
        field: "user",
        headerName: translate("v1/usage_admin.fields.user"),
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/users/edit/${value}`}
            value={users?.find(user => user.id === value)?.full_name}
          />
        ),
      },
      {
        field: "discount",
        headerName: translate("v1/usage_admin.fields.discount"),
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/agreement/edit/${value}`}
            value={discounts?.find(discount => discount.id === value)?.description}
          />
        ),
      },
      {
        field: "store",
        headerName: translate("v1/usage_admin.fields.store"),
        minWidth: 120,
        flex: 1,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={value}
            textAlign="left"
          />
        ),
      },
      {
        field: "saved",
        headerName: translate("v1/usage_admin.fields.saved"),
        minWidth: 120,
        headerAlign: "right",
        renderCell: ({ value }) => (
          <TableCell.Text value={`R$ ${value?.toFixed(2)}`}
          />
        ),
      },
      {
        field: "payment_status",
        headerName: translate("v1/usage_admin.fields.payment_status"),
        minWidth: 140,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Text value={value || "-"} textAlign="center" />
        ),
      },
      {
        field: "created_at",
        headerName: translate("v1/usage_admin.fields.created_at"),
        minWidth: 180,
        headerAlign: "center",
        renderCell: ({ value }) => (
          <TableCell.Date value={value} align="center" />
        ),
      },
    ],
    [translate, companies, agreements, users, discounts],
  );
};
